import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';

const ToastContext = createContext({ notify: () => {} });

export const useToast = () => useContext(ToastContext);

const ToastProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState({ title: '', description: '', type: 'default' });

  const notify = useCallback(({ title, description, type = 'default' }) => {
    const stripNumbers = (s) => {
      if (typeof s !== 'string') return s;
      const noNums = s.replace(/[0-9]+/g, '');
      return noNums.replace(/\s{2,}/g, ' ').trim();
    };
    setToast({ title: stripNumbers(title), description: stripNumbers(description), type });
    setOpen(false);
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        <style>{`
          :root { --vt-viewport-padding: 12px; }
          .vt-toast-viewport { outline: none; list-style: none; margin: 0; padding: 0; }
          .vt-toast-root { will-change: transform, opacity; }
          @keyframes vt-hide { from { opacity: 1; } to { opacity: 0; } }
          @keyframes vt-slide-in { from { transform: translateX(calc(100% + var(--vt-viewport-padding))); } to { transform: translateX(0); } }
          @keyframes vt-swipe-out { from { transform: translateX(var(--radix-toast-swipe-end-x)); } to { transform: translateX(calc(100% + var(--vt-viewport-padding))); } }
          .vt-toast-root[data-state="open"] { animation: vt-slide-in 180ms cubic-bezier(0.16, 1, 0.3, 1); }
          .vt-toast-root[data-state="closed"] { animation: vt-hide 120ms ease-in forwards; }
          .vt-toast-root[data-swipe="move"] { transform: translateX(var(--radix-toast-swipe-move-x)); }
          .vt-toast-root[data-swipe="cancel"] { transform: translateX(0); transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1); }
          .vt-toast-root[data-swipe="end"] { animation: vt-swipe-out 120ms ease-out forwards; }
        `}</style>
        {children}
        <ToastPrimitive.Root className="vt-toast-root" open={open} onOpenChange={setOpen} duration={3200} style={{
          background: '#ffffff',
          color: '#0f172a',
          borderRadius: 12,
          padding: '14px 16px',
          boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
          border: '1px solid rgba(15,23,42,0.08)',
          borderLeft: `4px solid ${toast.type === 'success' ? '#16a34a' : (toast.type === 'error' ? '#ef4444' : '#004d54')}`
        }}>
          {toast.title ? (
            <ToastPrimitive.Title style={{ fontWeight: 700, fontSize: 15, lineHeight: '22px', marginBottom: 6 }}>{toast.title}</ToastPrimitive.Title>
          ) : null}
          {toast.description ? (
            <ToastPrimitive.Description style={{ opacity: 0.95, fontWeight: 600, fontSize: 14, lineHeight: '21px' }}>{toast.description}</ToastPrimitive.Description>
          ) : null}
        </ToastPrimitive.Root>
        <ToastPrimitive.Viewport className="vt-toast-viewport" style={{
          position: 'fixed',
          top: 24,
          right: 12,
          width: 360,
          maxWidth: '90vw',
          zIndex: 9999,
          listStyle: 'none',
          margin: 0,
          padding: 0
        }} />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
};

export default ToastProvider;


